import React, { useState, useEffect } from 'react';
// Uppercase alias so the JSX-only usage passes this repo's no-unused-vars
// config (varsIgnorePattern ^[A-Z_]), which doesn't count JSX references.
import { motion as Motion } from 'framer-motion';
import {
    Inbox, Check, X as XIcon, FlaskConical, ScanLine, Building2,
    ClipboardList,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { fetchProposals, decideProposal } from '../../redux/masterCatalogSlice';
import { getTenantOptions } from '../../redux/tenantSlice';
import { MASTER_CATALOG_KINDS } from '../../data/masterCatalogKinds';
import useCanManage from '../../hooks/useCanManage';

// SUPER ADMIN review queue for propose-to-master (Phase 2, decisions log #5).
// A hospital found something missing from the world catalog and sent its own
// item up; approving creates the master row (or converges on one that appeared
// since), rejecting stamps a note the hospital can read. Either way the
// hospital KEEPS its private item — this queue only decides what the rest of
// the world sees.

const KIND_ICONS = { lab_test: FlaskConical, imaging_procedure: ScanLine };
const STATUS_STYLES = {
    pending: 'bg-amber-100 text-amber-700 border-amber-300',
    approved: 'bg-green-100 text-green-700 border-green-300',
    rejected: 'bg-gray-200 text-gray-500 border-gray-300',
};

const CatalogProposals = () => {
    const { t } = useTranslation('mastercatalog');
    const dispatch = useDispatch();
    // Approve / reject are writes (masters.manage); a view-only platform staff
    // member reads the queue without the buttons (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');
    const { proposals, decideLoading } = useSelector((s) => s.masterCatalog);
    // A proposal carries only the proposing hospital's tenantId. That id is
    // never drawn (owner rule, 29 Sep 2026), so the name is looked up in the
    // shared tenant options; without them the row says "Hospital".
    const tenantOptions = useSelector((s) => s.tenant?.tenantOptions);
    const hospitalName = (tenantId) =>
        (tenantOptions || []).find((x) => x.tenantId === tenantId)?.name || t('moc.hr_detail_hospital', 'Hospital');

    const [statusTab, setStatusTab] = useState('pending');
    const [kindFilter, setKindFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => { setCurrentPage(1); }, [statusTab, kindFilter]);

    useEffect(() => {
        if (!(tenantOptions || []).length) dispatch(getTenantOptions());
        // Once on mount: the options list is shared and rarely changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchProposals({ kind: kindFilter, status: statusTab, page: currentPage, limit: itemsPerPage }));
    }, [dispatch, statusTab, kindFilter, currentPage, itemsPerPage]);

    const refresh = () => dispatch(fetchProposals({ kind: kindFilter, status: statusTab, page: currentPage, limit: itemsPerPage }));

    const handlePageChange = (page, newLimit) => {
        if (newLimit && newLimit !== itemsPerPage) { setItemsPerPage(newLimit); setCurrentPage(1); }
        else setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const decide = async (proposal, approve) => {
        let reviewNote = '';
        if (!approve) {
            const { value, isConfirmed } = await Swal.fire({
                title: t('proposals.reject_title', 'Reject this proposal?'),
                input: 'text',
                inputLabel: t('proposals.reject_note', 'A short note the hospital will see (optional)'),
                inputPlaceholder: t('proposals.reject_placeholder', 'e.g. already covered by the CBC panel'),
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                confirmButtonText: t('proposals.reject_confirm', 'Reject'),
                cancelButtonText: t('mastercatalog.cancel', 'Cancel'),
            });
            if (!isConfirmed) return;
            reviewNote = value || '';
        }
        try {
            const out = await dispatch(decideProposal({
                proposalId: proposal.proposalId, approve, reviewNote,
            })).unwrap();
            toast.success(approve
                ? t('proposals.approved', { name: out.item?.name || proposal.payload?.name, defaultValue: `"${out.item?.name || proposal.payload?.name}" is now in the master` })
                : t('proposals.rejected', 'Proposal rejected'));
            refresh();
        } catch (err) {
            toast.warn(err?.error || t('proposals.decide_failed', 'Could not decide the proposal'));
        }
    };

    const list = proposals.list || [];

    return (
        <div className="w-full" data-testid="catalog-proposals-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex items-center gap-3 ml-2 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md">
                        <ClipboardList size={22} className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="catalog-proposals-title">
                            {t('proposals.title', 'Catalog Proposals')}
                        </h1>
                        <p className="text-xs text-gray-400">
                            {t('proposals.subtitle', 'Items hospitals want added to the world catalogs. They keep their copy either way.')}
                        </p>
                    </div>
                </div>

                {/* Status tabs + kind filter */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                        {['pending', 'approved', 'rejected'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setStatusTab(tab)}
                                className={`relative px-5 py-2 text-sm rounded-xl transition-colors duration-300 z-10 ${statusTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                                data-testid={`catalog-proposals-tab-${tab}`}
                            >
                                {statusTab === tab && (
                                    <Motion.div
                                        layoutId="proposalStatusTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20 capitalize">{t(`proposals.${tab}`, tab)}</span>
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setKindFilter('')}
                            className={`px-3 py-1.5 text-xs rounded-full border transition-colors duration-200 ${kindFilter === '' ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]' : 'text-gray-500 border-gray-300 hover:border-[#4ABDBD] hover:text-[#4ABDBD]'}`}
                        >
                            {t('proposals.all_kinds', 'All')}
                        </button>
                        {Object.entries(MASTER_CATALOG_KINDS).map(([k, def]) => (
                            <button
                                key={k}
                                onClick={() => setKindFilter(k)}
                                className={`px-3 py-1.5 text-xs rounded-full border transition-colors duration-200 ${kindFilter === k ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]' : 'text-gray-500 border-gray-300 hover:border-[#4ABDBD] hover:text-[#4ABDBD]'}`}
                                data-testid={`catalog-proposals-kind-${k}`}
                            >
                                {t(`mastercatalog.title_${k}`, def.labelPlural)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* List */}
                {proposals.loading ? (
                    <div className="space-y-3" data-testid="catalog-proposals-skeleton">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                                <div className="h-3 bg-gray-100 rounded w-2/3"></div>
                            </div>
                        ))}
                    </div>
                ) : list.length === 0 ? (
                    <div className="text-center py-16" data-testid="catalog-proposals-empty">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Inbox size={44} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700">
                            {statusTab === 'pending'
                                ? t('proposals.none_pending', 'Nothing waiting for review')
                                : t('proposals.none_here', 'Nothing here yet')}
                        </h3>
                    </div>
                ) : (
                    <>
                    <div className="space-y-3" data-testid="catalog-proposals-list">
                        {list.map((p) => {
                            const def = MASTER_CATALOG_KINDS[p.kind];
                            const Icon = KIND_ICONS[p.kind] || Inbox;
                            const payload = p.payload || {};
                            const detailFields = (def?.fields || [])
                                .filter((f) => f.key !== 'name' && payload[f.key] !== undefined && payload[f.key] !== null && String(payload[f.key]).trim() !== '');
                            return (
                                <div
                                    key={p.proposalId}
                                    className="group border border-gray-200 rounded-lg p-4 hover:border-[#4ABDBD] hover:shadow-md transition-all duration-300"
                                    data-testid={`catalog-proposal-${p.proposalId}`}
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#4ABDBD]/15 to-[#2D8A8A]/15 flex items-center justify-center shrink-0">
                                            <Icon size={18} className="text-[#2D8A8A]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <h3 className="text-base font-bold text-gray-800">{payload.name}</h3>
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                                                    {t(`mastercatalog.title_${p.kind}`, def?.label || p.kind)}
                                                </span>
                                                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold capitalize ${STATUS_STYLES[p.status] || STATUS_STYLES.rejected}`}>
                                                    {t(`proposals.${p.status}`, p.status)}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mb-1">
                                                {detailFields.slice(0, 6).map((f) => (
                                                    <span key={f.key}>
                                                        <span className="text-gray-400">{t(`mastercatalog.field_${f.key}`, f.label)}:</span>{' '}
                                                        {f.type === 'checkbox' ? (payload[f.key] ? t('mastercatalog.yes', 'Yes') : t('mastercatalog.no', 'No')) : String(payload[f.key])}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-400">
                                                <span className="inline-flex items-center gap-1"><Building2 size={12} /> {hospitalName(p.tenantId)}</span>
                                                {p.created_at && <span>{new Date(p.created_at).toLocaleDateString()}</span>}
                                                {p.status !== 'pending' && p.reviewNote && (
                                                    <span className="italic">“{p.reviewNote}”</span>
                                                )}
                                            </div>
                                        </div>
                                        {canManage && p.status === 'pending' && (
                                            <div className="flex gap-2 shrink-0">
                                                <button
                                                    onClick={() => decide(p, true)}
                                                    disabled={decideLoading}
                                                    className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50"
                                                    data-testid={`catalog-proposal-approve-${p.proposalId}`}
                                                >
                                                    <Check size={16} /> {t('proposals.approve', 'Approve')}
                                                </button>
                                                <button
                                                    onClick={() => decide(p, false)}
                                                    disabled={decideLoading}
                                                    className="flex items-center gap-1.5 px-3 py-2 border-2 border-gray-300 text-gray-600 text-sm rounded-lg hover:border-red-400 hover:text-red-500 transition-colors duration-300 disabled:opacity-50"
                                                    data-testid={`catalog-proposal-reject-${p.proposalId}`}
                                                >
                                                    <XIcon size={16} /> {t('proposals.reject', 'Reject')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {proposals.count > itemsPerPage && (
                        <div className="mt-4">
                            <Pagination
                                totalItems={proposals.count}
                                currentPage={proposals.page || currentPage}
                                itemsPerPage={proposals.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CatalogProposals;
